import React, { useState } from 'react';
import { Modal, message } from 'antd';
import {
    TrophyOutlined, FireOutlined, ClockCircleOutlined, BookOutlined,
    EditOutlined, CheckCircleOutlined, CalendarOutlined,
} from '@ant-design/icons';

const STORAGE_KEY = 'learningGoal_v1';

const DEFAULT_GOAL = {
    lessonsPerDay:  1,
    lessonsPerWeek: 5,
    minutesPerDay:  30,
    streakTarget:   14,
};

const PRESETS = [
    {
        label: '🌱 Cơ bản',
        desc: 'Người mới bắt đầu',
        value: { lessonsPerDay: 1, lessonsPerWeek: 3, minutesPerDay: 15, streakTarget: 7 },
    },
    {
        label: '🚀 Tiêu chuẩn',
        desc: 'Học viên đều đặn',
        value: { lessonsPerDay: 1, lessonsPerWeek: 5, minutesPerDay: 30, streakTarget: 14 },
    },
    {
        label: '⚡ Chuyên sâu',
        desc: 'Tăng tốc tối đa',
        value: { lessonsPerDay: 2, lessonsPerWeek: 10, minutesPerDay: 60, streakTarget: 30 },
    },
];

function loadGoal() {
    try {
        const s = localStorage.getItem(STORAGE_KEY);
        return s ? { ...DEFAULT_GOAL, ...JSON.parse(s) } : DEFAULT_GOAL;
    } catch { return DEFAULT_GOAL; }
}

/** +/- row input for a single goal field */
function GoalInput({ label, icon, value, onChange, min = 1, max, unit }) {
    return (
        <div className="flex items-center justify-between py-4 border-b border-white/10 last:border-0">
            <div className="flex items-center gap-3">
                <span>{icon}</span>
                <span className="text-gray-300 text-sm font-medium">{label}</span>
            </div>
            <div className="flex items-center gap-3">
                <button
                    onClick={() => onChange(Math.max(min, value - 1))}
                    disabled={value <= min}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >−</button>
                <div className="w-20 text-center">
                    <span className="text-white text-xl font-bold">{value}</span>
                    <span className="text-gray-500 text-xs ml-1">{unit}</span>
                </div>
                <button
                    onClick={() => onChange(Math.min(max, value + 1))}
                    disabled={value >= max}
                    className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 text-white font-bold text-lg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >+</button>
            </div>
        </div>
    );
}

/** Small colored chip for the banner */
function Chip({ icon, text, color }) {
    const map = {
        blue:   'bg-blue-500/10 border-blue-500/20 text-blue-300',
        purple: 'bg-purple-500/10 border-purple-500/20 text-purple-300',
        green:  'bg-green-500/10 border-green-500/20 text-green-300',
        orange: 'bg-orange-500/10 border-orange-500/20 text-orange-300',
    };
    return (
        <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-sm font-semibold ${map[color]}`}>
            {icon}<span>{text}</span>
        </div>
    );
}

export default function LearningGoal() {
    const [open, setOpen] = useState(false);
    const [goal, setGoal] = useState(loadGoal);
    const [draft, setDraft] = useState(goal);

    const set = (key) => (val) => setDraft(d => ({ ...d, [key]: val }));

    const isPreset = (p) =>
        p.value.lessonsPerDay  === draft.lessonsPerDay  &&
        p.value.lessonsPerWeek === draft.lessonsPerWeek &&
        p.value.minutesPerDay  === draft.minutesPerDay  &&
        p.value.streakTarget   === draft.streakTarget;

    const saveGoal = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
        setGoal(draft);
        setOpen(false);
        message.success('Đã lưu mục tiêu học!');
    };

    return (
        <>
            {/* ── Banner hiển thị mục tiêu hiện tại ── */}
            <div className="mb-8 bg-[#0f1e35] border border-white/10 rounded-2xl px-6 py-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-3 flex items-center gap-2">
                            <TrophyOutlined className="text-yellow-400" /> Mục tiêu học của bạn
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Chip icon={<BookOutlined className="text-blue-400" />}
                                text={`${goal.lessonsPerDay} bài/ngày`} color="blue" />
                            <Chip icon={<CalendarOutlined className="text-purple-400" />}
                                text={`${goal.lessonsPerWeek} bài/tuần`} color="purple" />
                            <Chip icon={<ClockCircleOutlined className="text-green-400" />}
                                text={`${goal.minutesPerDay} phút/ngày`} color="green" />
                            <Chip icon={<FireOutlined className="text-orange-400" />}
                                text={`Streak ${goal.streakTarget} ngày`} color="orange" />
                        </div>
                    </div>
                    <button
                        onClick={() => { setDraft(goal); setOpen(true); }}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-500/20 text-sm whitespace-nowrap self-start sm:self-auto"
                    >
                        <EditOutlined /> Đặt mục tiêu
                    </button>
                </div>
            </div>

            {/* ── Modal ── */}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                onOk={saveGoal}
                okText="Lưu mục tiêu"
                cancelText="Hủy"
                okButtonProps={{ className: '!bg-blue-500 !border-blue-500 !text-white' }}
                title={
                    <div className="flex items-center gap-2 text-white">
                        <TrophyOutlined className="text-yellow-400" />
                        <span>Đặt mục tiêu học tập</span>
                    </div>
                }
                className="[&_.ant-modal-content]:bg-[#0f1e35] [&_.ant-modal-content]:border [&_.ant-modal-content]:border-white/10 [&_.ant-modal-header]:bg-transparent [&_.ant-modal-title]:text-white [&_.ant-btn-default]:text-gray-300 [&_.ant-btn-default]:border-gray-500 [&_.ant-modal-footer]:border-white/10"
                width={460}
            >
                {/* Presets */}
                <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-3 mt-4">Chọn nhanh</p>
                <div className="flex gap-2 flex-wrap mb-6">
                    {PRESETS.map((p, i) => (
                        <button
                            key={i}
                            onClick={() => setDraft(p.value)}
                            className={`flex-1 min-w-[110px] px-3 py-2.5 rounded-xl text-sm border transition-all text-left ${
                                isPreset(p)
                                    ? 'bg-blue-500 text-white border-blue-500 shadow-md shadow-blue-500/20'
                                    : 'bg-white/5 text-gray-300 border-white/10 hover:border-blue-400 hover:text-blue-300'
                            }`}
                        >
                            <p className="font-semibold">{p.label}</p>
                            <p className="text-xs opacity-70 mt-0.5">{p.desc}</p>
                        </button>
                    ))}
                </div>

                {/* +/- inputs */}
                <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold mb-2">Tuỳ chỉnh</p>
                <div className="bg-[#1a2d4a] rounded-2xl px-5">
                    <GoalInput
                        label="Số bài học mỗi ngày"
                        icon={<BookOutlined className="text-blue-400" />}
                        value={draft.lessonsPerDay}
                        onChange={set('lessonsPerDay')}
                        min={1} max={10} unit="bài"
                    />
                    <GoalInput
                        label="Số bài học mỗi tuần"
                        icon={<CalendarOutlined className="text-purple-400" />}
                        value={draft.lessonsPerWeek}
                        onChange={set('lessonsPerWeek')}
                        min={1} max={30} unit="bài"
                    />
                    <GoalInput
                        label="Thời gian học mỗi ngày"
                        icon={<ClockCircleOutlined className="text-green-400" />}
                        value={draft.minutesPerDay}
                        onChange={set('minutesPerDay')}
                        min={5} max={180} unit="phút"
                    />
                    <GoalInput
                        label="Mục tiêu streak"
                        icon={<FireOutlined className="text-orange-400" />}
                        value={draft.streakTarget}
                        onChange={set('streakTarget')}
                        min={1} max={365} unit="ngày"
                    />
                </div>

                {/* Summary */}
                <div className="mt-5 bg-blue-500/10 border border-blue-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
                    <CheckCircleOutlined className="text-blue-400 mt-0.5 flex-shrink-0" />
                    <p className="text-gray-300 text-xs leading-relaxed">
                        Mỗi ngày học <strong className="text-white">{draft.lessonsPerDay} bài</strong>
                        {' · '}
                        <strong className="text-white">{draft.lessonsPerWeek} bài/tuần</strong>
                        {' · '}
                        <strong className="text-white">{draft.minutesPerDay} phút</strong>/ngày
                        {' — streak '}
                        <strong className="text-white">{draft.streakTarget} ngày</strong>.
                    </p>
                </div>
            </Modal>
        </>
    );
}